import { Select } from '@mantine/core'
import type { TLngLat } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { groupBy } from 'lodash'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { dropRepeatedSelectValues } from 'utils/dropRepeatedSelectValues'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import type { TSelectItemWithExternalVendors } from 'utils/makeExternalVendorSelectProps'
import { makeExternalVendorSelectProps } from 'utils/makeExternalVendorSelectProps'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

export const StationSelect = ({
  selectedStation,
  onStationChange,
}: {
  selectedStation: TLngLat | null
  onStationChange: (value: TLngLat) => void
}) => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const selectClickToggle = useSelectClickToggle()

  const flatStationOptions: (TSelectItemWithExternalVendors & { group: string })[] = Object.values(
    properties ?? {},
  )
    .flatMap((property) => {
      if (!property.points) return []

      return Object.entries(property.points)
        .filter(([, point]) => point.configuration?.soilProbeDepthsAvailable?.length)
        .map(([lngLat, point]) => ({
          value: lngLat,
          label: point.name,
          externalVendorNames: getPointExternalVendorNames({ point, deviceKind: 'soil' }),
          group: property.propertyName,
        }))
    })
    .sort(sortByKey('label'))
    .sort(sortByKey('group'))

  const stationOptions = Object.entries(groupBy(flatStationOptions, 'group')).map(([group, options]) => ({
    group,
    items: options.map(({ value, label, externalVendorNames }) => ({ value, label, externalVendorNames })),
  }))

  const data = dropRepeatedSelectValues(stationOptions)
  const { selectProps, inputPaddingRight } = makeExternalVendorSelectProps({ data, value: selectedStation })

  return (
    <Select
      {...selectClickToggle}
      clearable={false}
      w={300}
      onChange={(value) => {
        if (value) onStationChange(value as TLngLat)
      }}
      data={data}
      {...selectProps}
      placeholder={translate.phrases.banyanApp('Select a Station')}
      filter={mantineSelectSearchFilterProp}
      searchable
      size="xs"
      spellCheck="false"
      classNames={SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_CLASSNAMES}
      styles={{
        ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
        ...SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_STYLES,
        dropdown: { marginTop: -6, fontWeight: 'normal' },
        input: { ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input, paddingRight: inputPaddingRight },
      }}
      value={selectedStation}
      comboboxProps={{
        withinPortal: true,
        zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts + 10000,
      }}
    />
  )
}
